import { CartItemAddInput } from '@/features/graphql/generated/graphql'
import { useCartKeyContext } from '@/features/pos/components/Cart/contexts/CartKey'
import { cartItemAddMutation } from '@/features/pos/components/Cart/mutations'
import {
    getCartFromCache,
    writeCartItemsToCache,
    writeCartQueryToCache,
} from '@/features/pos/components/Cart/utils/cart-cache'
import { appendCartItem } from '@/features/pos/components/Cart/utils/cart-item'
import { logger } from '@/features/pos/logger'
import { useHandleErrorWithToast } from '@/hooks/useHandleErrorWithToast'
import { useMutation } from '@apollo/client'
import { useCallback } from 'react'

export const useAddCartItem = (): ((item: CartItemAddInput) => Promise<boolean>) => {
    const { cartKey, setCartKey } = useCartKeyContext()

    const handleError = useHandleErrorWithToast(logger)

    const [cartItemAdd] = useMutation(cartItemAddMutation, {
        fetchPolicy: 'no-cache',
        update: (cache, { data }) => {
            const resCartKey = data?.cartItemAdd?.key
            const resItems = data?.cartItemAdd?.items
            const resContainerItems = data?.cartItemAdd?.containerItems

            if (!resCartKey || !resItems || !resContainerItems) return

            if (resCartKey !== cartKey) {
                setCartKey(resCartKey)

                return writeCartQueryToCache(cache, resCartKey, resItems, resContainerItems)
            }

            const currentCart = getCartFromCache(cache, cartKey)
            const newItem = resItems[resItems.length - 1]

            writeCartItemsToCache(cache, resCartKey, appendCartItem(currentCart?.items, newItem))
        },
        onError: handleError,
    })

    return useCallback(
        async (item: CartItemAddInput) => {
            const { data } = await cartItemAdd({
                variables: { itemAdd: item, cartKey },
            })

            return !!data?.cartItemAdd
        },
        [cartKey, cartItemAdd]
    )
}
